"use client"

import { useState, useEffect, useRef } from "react"
import { X, ArrowLeft } from "lucide-react"
import { getEmailProcessingInstructionService } from "../../utils/userService"

const EmailProcessingInstructions = ({ isOpen, onClose, onBack, onSave }) => {
  const getUserPreferencesCalled = useRef(false)

  const [preferences, setPreferences] = useState({
    preference_1: "",
    preference_2: "",
    preference_3: "",
    style_tone: "",
  })

  const [errors, setErrors] = useState({
    preference_1: false,
    preference_2: false,
    preference_3: false,
    style_tone: false,
  })

  useEffect(() => {
    const fetchUserPreferences = async () => {
      getUserPreferencesCalled.current = true

      try {
        const emailProcessingInstructions = await getEmailProcessingInstructionService()
        setPreferences(emailProcessingInstructions)
      } catch (error) {
        console.error("Error fetching user preferences:", error)
      } finally {
        getUserPreferencesCalled.current = false
      }
    }
    if (!getUserPreferencesCalled.current) {
      fetchUserPreferences()
    }
  }, [])

  const handleInputChange = (e) => {
    const { name, value } = e.target
    if (value.length <= 250) {
      setPreferences((prev) => ({
        ...prev,
        [name]: value,
      }))
      setErrors((prev) => ({
        ...prev,
        [name]: false,
      }))
    } else {
      setErrors((prev) => ({
        ...prev,
        [name]: true,
      }))
    }
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    onSave(preferences)
    onClose()
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex justify-center items-center z-[1000]">
      <div className="bg-white p-8 rounded-lg w-[90%] max-w-[600px] shadow-md">
        <div className="flex justify-between items-start mb-6">
          <div className="flex items-start gap-3">
            <button
              onClick={onBack}
              data-amp-track-section="Email Processing Instructions"
              data-amp-track-action="Back_Click"
              data-amp-track-description="Back to Email Preferences"
              className="bg-transparent border-none hover:bg-gray-100 rounded-lg p-2 cursor-pointer text-gray-600 hover:text-gray-800"
            >
              <ArrowLeft size={20} />
            </button>
            <div className="flex flex-col gap-2">
              <h2 className="m-0 text-gray-800">Custom Email Processing Instructions</h2>
              <p className="text-gray-600 text-sm">
                Customize <i>how</i> Geoffrey processes your email. These instructions
                will be taken into account when processing.
              </p>
            </div>
          </div>
          <button
            className="bg-transparent border-none hover:bg-gray-100 rounded-lg p-2 text-2xl cursor-pointer text-gray-600 hover:text-gray-800"
            onClick={onClose}
            data-amp-track-section="Email Processing Instructions"
            data-amp-track-action="Close_Click"
            data-amp-track-description="Close Email Processing Instructions"
          >
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div className="flex flex-col gap-4">
            <div className="mb-4 relative">
              <label htmlFor="style_tone" className="block mb-2 text-gray-800 text-base font-semibold">
                Style &amp; Tone
              </label>
              <input
                type="text"
                id="style_tone"
                name="style_tone"
                value={preferences?.style_tone}
                onChange={handleInputChange}
                maxLength={80}
                placeholder="Ex. Professional, Casual, Brief, etc. (Optional)"
                className="w-full py-3 px-3 border border-gray-300 rounded-md text-base focus:outline-none focus:border-blue-500 focus:shadow-outline-blue"
              />
              {errors.style_tone && (
                <span className="text-red-600 text-sm mt-1 block">Maximum 80 characters allowed</span>
              )}
              <span className="absolute right-0 top-full text-xs text-gray-600 mt-1">
                {preferences?.style_tone?.length || 0}/80
              </span>
            </div>
            
            <div className="mb-4 relative">
              <label htmlFor="preference_1" className="block mb-2 text-gray-800 text-base font-semibold">
                Reply & Maintenance Ticket Overrides
              </label>
              <textarea
                id="preference_1"
                name="preference_1"
                value={preferences?.preference_1}
                onChange={handleInputChange}
                maxLength={250}
                rows={3}
                placeholder="Ex. Never reply to auto-generated emails from PMS tools like AppFolio or RentManager. For maintenance issues, if unclear, ask for clarification before creating a ticket."
                className="w-full py-3 px-3 border border-gray-300 rounded-md text-base focus:outline-none focus:border-blue-500 focus:shadow-outline-blue"
              />
              {errors.preference_1 && (
                <span className="text-red-600 text-sm mt-1 block">Maximum 250 characters allowed</span>
              )}
              <span className="absolute right-0 top-full text-xs text-gray-600 mt-1">
                {preferences?.preference_1?.length || 0}/250
              </span>
            </div>

            <div className="mb-4 relative">
              <label htmlFor="preference_2" className="block mb-2 text-gray-800 text-base font-semibold">
                Task & Move Action Overrides
              </label>
              <textarea
                id="preference_2"
                name="preference_2"
                value={preferences?.preference_2}
                onChange={handleInputChange}
                maxLength={250}
                rows={3}
                placeholder="Ex. If the sender is my boss (John Smith <john.smith@example.com>), always treat as high priority. Always create a task if the email mentions upcoming deadlines."
                className="w-full py-3 px-3 border border-gray-300 rounded-md text-base focus:outline-none focus:border-blue-500 focus:shadow-outline-blue"
              />
              {errors.preference_2 && (
                <span className="text-red-600 text-sm mt-1 block">Maximum 250 characters allowed</span>
              )}
              <span className="absolute right-0 top-full text-xs text-gray-600 mt-1">
                {preferences?.preference_2?.length || 0}/250
              </span>
            </div>

            <div className="mb-4 relative">
              <label htmlFor="preference_3" className="block mb-2 text-gray-800 text-base font-semibold">
                General Processing Preferences
              </label>
              <textarea
                id="preference_3"
                name="preference_3"
                value={preferences?.preference_3}
                onChange={handleInputChange}
                maxLength={250}
                rows={3}
                placeholder="Ex. This is not an operational inbox and may include family, school, or personal related messages. Adjust processing to handle non-work correspondence appropriately."
                className="w-full py-3 px-3 border border-gray-300 rounded-md text-base focus:outline-none focus:border-blue-500 focus:shadow-outline-blue"
              />
              {errors.preference_3 && (
                <span className="text-red-600 text-sm mt-1 block">Maximum 250 characters allowed</span>
              )}
              <span className="absolute right-0 top-full text-xs text-gray-600 mt-1">
                {preferences?.preference_3?.length || 0}/250
              </span>
            </div>
          </div>


          <div className="flex justify-end gap-2 mt-8">
            <button
              type="button"
              className="py-3 px-6 rounded-md text-base cursor-pointer transition-colors duration-200 bg-gray-100 border border-gray-300 text-gray-800 hover:bg-gray-200"
              onClick={onBack}
              data-amp-track-section="Email Processing Instructions"
              data-amp-track-action="Back_Click"
              data-amp-track-description="Back to Email Preferences"
            >
              Back
            </button>
            <button
              type="submit"
              className="py-3 px-6 rounded-md text-base cursor-pointer transition-colors duration-200 bg-sky-500 border-none text-white hover:bg-sky-600"
              data-amp-track-section="Email Processing Instructions"
              data-amp-track-action="Save_Click"
              data-amp-track-description="Save Email Processing Instructions"
            >
              Save Preferences
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default EmailProcessingInstructions
